import { BIEventIds } from './event-ids';
import { sendBi } from './send-request';
import { generateRandomGuid4, parseSiteMediaToken, get, noop, getCurrentTime } from '../../utils/utils';
import { getDurationBetweenMarks, getEntriesByName, getResourceTimingByUrl, now } from '../performance';
import { MediaType } from '../../constants/MediaType';
import { getUoUParams } from './uou';
function notifyResponseTime(adapter, eventName, data) {
  const {
    fileId,
    correlationId,
    isSuccess,
    isFromMediaStudio,
    responseFileId,
    request
  } = data;
  const responseURL = request && request.responseURL;
  const performanceEntry = getEntriesByName(responseURL).pop();
  if (!performanceEntry) {
    return;
  }
  const responseTime = performanceEntry.duration;
  const responseSize = performanceEntry.transferSize;
  if (!responseTime || !responseSize) {
    return;
  }
  const requestName = adapter.name + "(" + eventName + ")";
  const responseSpeed = responseSize / (responseTime / 1000);
  let metaSiteId = '';
  if (adapter.siteMediaToken) {
    try {
      const parsedInfo = parseSiteMediaToken(adapter.siteMediaToken);
      metaSiteId = parsedInfo.metaSiteId.replace(/_template_/, '');
    } catch (error) {}
  }
  sendBi('/mg', {
    src: 26,
    evid: BIEventIds.BIEventsIds.responseTime,
    msid: metaSiteId,
    fileId,
    correlation_id: correlationId,
    call_name: requestName,
    result: isSuccess ? 'success' : 'fail',
    response_speed: Math.round(responseSpeed),
    response_time: Math.round(responseTime),
    response_size: responseSize,
    ...(responseFileId ? {
      responseFileId
    } : {}),
    ...(isFromMediaStudio ? {
      mode: 'media_studio'
    } : {})
  });
}
function getFileIdFromParams(params) {
  return (params.arguments || []).find(value => typeof value === 'string');
}
function getCorrelationIdFromParams(params, defaultValue) {
  const objectWithCorrelationId = (params.arguments || []).find(value => value && typeof value.correlationId !== 'undefined');
  return get(objectWithCorrelationId, ['correlationId'], defaultValue);
}
export function benchmarkResponse(promise, params) {
  const fileId = getFileIdFromParams(params) || null;
  const adapter = params.eventTarget;
  const correlationId = getCorrelationIdFromParams(params, adapter.correlationId ? adapter.correlationId.get() : generateRandomGuid4());
  const isFromMediaStudio = get(params, ['arguments', '1', 'isFromMediaStudio']);
  const notify = (isSuccess, result) => {
    notifyResponseTime(adapter, params.eventName, {
      fileId,
      correlationId,
      request: result.request,
      isSuccess,
      isFromMediaStudio,
      responseFileId: get(result, ['data', 'id'])
    });
  };
  promise.then(function (result) {
    notify(true, result);
  }).catch(function (reason) {
    notify(false, reason);
  });
}
export function benchmarkUpload(promise, params) {
  const startedAt = getCurrentTime();
  const fileSize = params.arguments[0].file.size;
  if (fileSize < 10240) {
    return;
  }
  promise.then(function () {
    const responseTime = (getCurrentTime() - startedAt) / 1000;
    const uOuParams = getUoUParams();
    if (responseTime < 1) {
      return;
    }
    sendBi('/mg', {
      src: 26,
      evid: BIEventIds.BIEventsIds.uploadSpeed,
      time_seconds: Math.floor(responseTime),
      size_bytes: fileSize,
      bytes_per_second: Math.floor(fileSize / responseTime),
      ...uOuParams()
    });
  }).catch(noop);
}
function mapUploadTimings(uploadTimingsHeaders) {
  if (!uploadTimingsHeaders) {
    return {};
  }
  return uploadTimingsHeaders.split(' ').reduce(function (acc, uploadTiming) {
    const splitUploadTiming = uploadTiming.split('=');
    const key = splitUploadTiming[0];
    acc[key] = splitUploadTiming[1];
    return acc;
  }, {});
}
export const createUploadMarks = () => {
  const nowTs = now();
  return {
    requestStart: "client-start-" + nowTs,
    urlEnd: "url-end-" + nowTs,
    uploadEnd: "upload-end-" + nowTs,
    responseEnd: "client-end-" + nowTs
  };
};
export function sendUploadTimingsBiEvent(callName, _ref) {
  let {
    response,
    fileSource,
    uploadUrl,
    startProgressMark,
    endProgressMark,
    endResponseMark,
    uploadMarks
  } = _ref;
  // TODO: refactor mediaTypes to constants
  if (fileSource.mediaType !== MediaType.Picture) {
    return;
  }
  const serverUploadTimings = mapUploadTimings(response.uploadTimingsHeaders);
  const requestTime = Math.ceil(endProgressMark - startProgressMark) || 1;
  const entry = getResourceTimingByUrl(uploadUrl);
  const responseTime = entry ? entry.responseEnd - entry.responseStart : 0;
  const waitTime = Math.round(endResponseMark - endProgressMark - responseTime);
  const uploadSpeed = Math.floor(fileSource.file.size / requestTime / 1000);
  const isSomethingNaN = [waitTime, uploadSpeed, responseTime].some(isNaN);
  if (isSomethingNaN) {
    return;
  }

  /**
   * new and updated metrics:
   *
   * requestStart            urlEnd        uploadEnd   google        responseEnd
   *       ^                   ^               ^        ^                ^
   *       |-------------------|---------------|--------|----------------|
   *       |   get_upload_url  |  new_request  | google | upload_handler |
   *       |                   |               |       processing        |
   *       |                   |            total_processing             |
   *       |                           total                             |
   */
  const uploadUrlTime = getDurationBetweenMarks(uploadMarks.requestStart, uploadMarks.urlEnd);
  const newRequestTime = getDurationBetweenMarks(uploadMarks.urlEnd, uploadMarks.uploadEnd);
  const processingTime = getDurationBetweenMarks(uploadMarks.uploadEnd, uploadMarks.responseEnd);
  const totalProcessingTime = newRequestTime + processingTime;
  const clientUploadTimings = {
    request_time: requestTime,
    wait_time: Math.max(waitTime, 0),
    response_time: Math.round(responseTime),
    upload_speed: uploadSpeed,
    // new and updated metrics
    get_upload_url_time: Math.round(uploadUrlTime),
    processing_time: Math.round(processingTime),
    total_processing_time: Math.round(totalProcessingTime),
    total_time: Math.round(uploadUrlTime + totalProcessingTime)
  };
  sendBi('/mg', {
    src: 26,
    evid: BIEventIds.BIEventsIds.apiTiming,
    call_name: callName,
    file_size: fileSource.file.size,
    ...clientUploadTimings,
    ...serverUploadTimings
  });
}